import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  ASSIGNMENT,
  linkedEvidence,
  nowIso,
  tagDiff,
  uuidv7,
  type StatusLog,
} from '@kilroy/shared';

import { db } from '../db/db';
import { useUsers } from '../hooks/useUsers';
import { commit } from '../db/commit';
import { useSession } from '../app/session';
import { EvidenceList } from '../components/EvidenceList';
import { IconCheck } from '../components/icons';
import './assignment.css';

/* One assignment, as the person walking it sees it.
 *
 * Two actions, and the second one matters as much as the first: "mark walked —
 * nothing further" turns a negative result into a record. Ground that was
 * checked and found barren is real information, and the only way it survives
 * is if closing an assignment is as easy as capturing against it.
 */

export function AssignmentScreen() {
  const { userById } = useUsers();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSession();
  const [closing, setClosing] = useState(false);

  const assignment = useLiveQuery(() => (id ? db.assignments.get(id) : undefined), [id]);
  const evidence = useLiveQuery(() => db.evidence.toArray(), [], []);

  if (!assignment) return <div className="screen" />;

  const author = userById(assignment.created_by);
  const found = linkedEvidence(assignment, evidence);
  const diff = tagDiff(assignment, found);
  const closed =
    assignment.current_status === 'completed' || assignment.current_status === 'dismissed';

  const expectedTags = [
    ...(assignment.expected_alteration_type ?? []),
    ...(assignment.expected_rock_type ? [assignment.expected_rock_type] : []),
    ...(assignment.expected_feature_type ? [assignment.expected_feature_type] : []),
  ];

  async function markWalked() {
    setClosing(true);
    const row: StatusLog = {
      id: uuidv7(),
      entity_type: 'field_assignment',
      entity_id: assignment!.id,
      status_field: 'current_status',
      old_status: assignment!.current_status,
      new_status: 'completed',
      changed_by: user.id,
      changed_at: nowIso(),
      note: 'walked — nothing further',
      sync_state: 'local',
    };
    // The projection is recomputed from the log; the cached column is written
    // alongside it purely so this screen updates now rather than after a pull.
    await db.assignments.update(assignment!.id, { current_status: 'completed' });
    await commit({ kind: 'status', record: row });
    navigate('/', { replace: true });
  }

  return (
    <div className="screen">
      <div className="screen__h">
        <p className="asg__from mono">
          {author.name} → {assignment.assigned_to ? userById(assignment.assigned_to).name : 'open to the team'}
        </p>
        <h1 className="screen__t">{assignment.title}</h1>
        <p className="screen__s">
          {assignment.assignment_type.replace(/_/g, ' ')} · {assignment.priority} priority ·{' '}
          {assignment.current_status.replace(/_/g, ' ')}
        </p>
      </div>

      <div className="asg__instructions">{assignment.instructions_text}</div>

      {expectedTags.length > 0 && (
        <section className="mod">
          <div className="mod__h">
            <h2 className="label">Expected</h2>
            <span className="mod__n">
              {diff.met.length}/{expectedTags.length}
            </span>
          </div>
          <div className="card card--pad">
          <div className="chiprow">
            {expectedTags.map((t) => (
              <span
                key={t}
                className={`chip chip--static${diff.met.includes(t) ? ' chip--met' : ''}`}
              >
                {diff.met.includes(t) && <IconCheck size={13} />}
                {t.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
          {diff.unexpected.length > 0 && (
            <p className="hint">
              Also found: {diff.unexpected.join(', ')} — worth a note on why.
            </p>
          )}
          </div>
        </section>
      )}

      <section className="mod">
        <div className="mod__h">
          <h2 className="label">Captured against this</h2>
          <span className="mod__n">{ASSIGNMENT.progress(found.length)}</span>
        </div>
        <EvidenceList
          evidence={found}
          empty="Nothing yet."
          hint="Anything you capture from here links back automatically."
        />
      </section>

      {!closed && (
        <div className="asg__actions">
          <button
            type="button"
            className="btn btn--primary btn--lg"
            onClick={() => navigate(`/capture?assignment=${assignment.id}`)}
          >
            {ASSIGNMENT.captureAgainst}
          </button>
          <button
            type="button"
            className="btn btn--quiet btn--lg"
            onClick={() => void markWalked()}
            disabled={closing}
          >
            {ASSIGNMENT.markWalked}
          </button>
          <p className="hint" style={{ textAlign: 'center' }}>
            {ASSIGNMENT.walkedPrompt}
          </p>
        </div>
      )}
    </div>
  );
}
